<template lang="pug">
.kr-help-button-wrapper(v-if="isVisible" :class="dynamicClasses.button")
  button.kr-help-button(
    ref="activator"
    type="button"
    :aria-label="label.activator"
    :disabled="!isVisible"
    :style="css"
    @click="openModal()"
  )
    .kr-help-icon-wrapper(:class="dynamicClasses.iconWrapper" :style="{backgroundColor: css.color}")
      template(v-if="customIcon")
        span(:class="dynamicClasses.icon" v-html="customIcon || icon")
      template(v-else)
        span(:class="dynamicClasses.icon")
          SvgImage(
            :title="label.activator"
            :decorative="false"
            :template="icon"
          )
  KryptonDialog(v-model="open" content-class="kr-help-button-wrapper")
    .kr-help-modal-wrapper(ref="modal")
      SmartFormTabGuard(position="first" :dom-reference="$refs.modal")
      .kr-help-modal-background
      .kr-help-modal(:style="[modalSize]" role="dialog" :aria-label="label.dialog" :aria-describedby="contentUid")
        .kr-help-modal-header
          button.kr-help-modal-close-button(
            type="button"
            :aria-label="label.close"
            :disabled="!open"
            @click="closeModal()"
            v-html="closeIcon"
          )
        HelpButtonContent(:id="contentUid" :field-name="fieldName")
      SmartFormTabGuard(position="last" :dom-reference="$refs.modal")
</template>

<script>
import _ from 'underscore'
import Zepto from 'zepto-webpack'
import { mapState, mapGetters } from 'vuex'
import { dynamicMapState } from '@/common/util/store'
import Events from '@/configuration/Events'
import PreloadedAssets from '@/configuration/PreloadedAssets'
import HelpButtonContent from '@/host/components/controls/HelpButtonContent'
import KryptonDialog from '@/host/components/controls/KryptonDialog'
import SvgImage from '@/common/components/SvgImage'
import SmartFormTabGuard from '@/host/components/smartform/TabGuard'
import { focusFirstDescendant } from '@/common/util/aria'
import { urlToSvg, ensureUniqId } from '@/common/util/svg'
import { isValidUrl } from '@/common/util/url'

/** MIXINS */
import ConfigurationMixin from '@/host/components/mixins/Configuration'
import { ClickOutsideMixin } from '@/host/components/mixins/ClickOutside'
import { MultipleBrandMixin } from '@/host/components/mixins/MultipleBrand'

/**
 * @see KJS-5012 | ARIA
 */
export default {
  name: 'KryptonHelpButton',
  components: { KryptonDialog, HelpButtonContent, SvgImage, SmartFormTabGuard },
  mixins: [ConfigurationMixin, ClickOutsideMixin, MultipleBrandMixin],
  inject: ['context'],
  props: {
    fieldName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: false,
      modalOpen: false,
      modalSize: {},
      forcedHide: false,
      closeIcon: PreloadedAssets.close.close_icon,
      customIcon: null
    }
  },
  computed: {
    ...mapGetters(['isFormPopin', 'isSmartForm', 'translate']),
    ...mapState(['fields', 'formToken']),
    ...mapState({ fieldIcons: state => state.fields.icons }),
    ...dynamicMapState('context.namespace', ['cardBrandHelpVisibility']),
    positionClass() {
      return 'kr-inner'
    },
    // Configuration
    css() {
      const field =
        this.fields[this.fieldName][this.isInvalid ? 'error' : 'default']
      return {
        backgroundColor: field.backgroundColor,
        color: field.iconColor
      }
    },
    isVisible() {
      if (this.forcedHide) return false
      if (this.fieldName === 'securityCode') return true
      if (this.fieldName === 'pan') {
        if (this.cardBrandHelpVisibility) return true
        if (this.cardBrandHelpVisibility === false) return false
        return this.hasMultipleBrands
      }
      return false
    },
    icon() {
      let icon = PreloadedAssets.help.help_icon
      if (this.css.color) {
        if (typeof PreloadedAssets.help.help_icon_style === 'function') {
          icon = PreloadedAssets.help.help_icon_style(icon, this.css)
        }
        icon = icon.replace(/fill=\"([^"]*)\"/g, `fill="${this.css.color}"`)
      }
      return icon
    },
    customIconUrl() {
      const url = this.fieldIcons?.help?.[this.fieldName]
      return url ?? null
    },
    label() {
      return {
        activator: this.translate('cvv_help_icon_tootip'),
        dialog: this.translate('label_help_dialog'),
        close: this.translate('close')
      }
    },
    contentUid() {
      return `kr-help-content-${this._uid}`
    },
    dynamicClasses() {
      return {
        button: {
          [this.positionClass]: true,
          'kr-visible': this.isVisible,
          'kr-disable': this.isDisable
        },
        iconWrapper: {
          'kr-error': this.isInvalid
        },
        icon: {
          'kr-custom-help-icon': this.customIcon,
          'kr-help-icon': !this.customIcon
        }
      }
    }
  },
  watch: {
    async customIconUrl() {
      await this.loadCustomIcon()
    },
    open(val) {
      if (val) {
        this.$nextTick(() => {
          focusFirstDescendant(this.$refs.modal)
        })
      } else {
        this.$refs.activator.focus()
      }
    }
  },
  async created() {
    this.startSubscriptions()

    // Shall load custom icon
    await this.loadCustomIcon()
  },
  methods: {
    startSubscriptions() {
      const _this = this

      // Keyboard Esc detection
      document.addEventListener('keyup', evt => {
        if (evt.hasOwnProperty('key')) {
          if (evt.key === 'Escape' || evt.key === 'Esc') _this.closeModal()
        } else if (evt.keyCode === 27) {
          // No evt.key support
          _this.closeModal()
        }
      })

      // Events
      this.$busOn(Events.krypton.form.help.visibility, ({ visibility }) => {
        if (!visibility) this.forcedHide = true
      })
    },
    openModal() {
      const _this = this
      this.setModalSize()
      if (this.isVisible) {
        this.open = true
        setTimeout(() => {
          _this.modalOpen = true
        }, 100)
      }
    },
    closeModal() {
      if (this.modalOpen) {
        this.open = false
        this.modalOpen = false
      }
    },
    setModalSize() {
      // If it's a popin form, fill the same space.
      if (this.isFormPopin && !this.isSmartForm) {
        let $embedded = Zepto('.kr-embedded')
        const hasLowerHeight = Zepto(window).height() <= $embedded.height()
        this.modalSize = {
          height: hasLowerHeight ? `100%` : `${$embedded.height()}px`,
          width: `${$embedded.width()}px`
        }
      }
    },
    async loadCustomIcon() {
      this.customIcon = isValidUrl(this.customIconUrl)
        ? await urlToSvg(this.customIconUrl)
        : ensureUniqId(this.customIconUrl)
    }
  }
}
</script>
